import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { IconType } from "react-icons";
import { signOut } from "firebase/auth";
import {
  FaCalendarAlt,
  FaCalendarCheck,
  FaCog,
  FaCommentDots,
  FaGlobe,
  FaHandsHelping,
  FaHome,
  FaLayerGroup,
  FaMoon,
  FaMusic,
  FaSignOutAlt,
  FaSun,
  FaUserCircle,
  FaUsers,
} from "react-icons/fa";
import { HiOutlineXMark } from "react-icons/hi2";
import { useTranslation } from "react-i18next";
import { auth } from "../db/firebase";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import SettingsModal from "../pages/Organization/Components/SettingsModal";

interface NavItem {
  to: string;
  labelKey: string;
  icon: IconType;
}

const MEMBER_ITEMS: NavItem[] = [
  { to: "/dashboard", labelKey: "home", icon: FaHome },
  { to: "/calendar", labelKey: "calendar", icon: FaCalendarCheck },
  { to: "/songs", labelKey: "songs", icon: FaMusic },
];

const ADMIN_ITEMS: NavItem[] = [
  { to: "/events", labelKey: "events", icon: FaCalendarAlt },
  { to: "/users", labelKey: "users", icon: FaUsers },
  { to: "/groups", labelKey: "groups", icon: FaLayerGroup },
  { to: "/ministries", labelKey: "ministries", icon: FaHandsHelping },
  { to: "/song-importer", labelKey: "songImporter", icon: FaGlobe },
  { to: "/communications", labelKey: "communications", icon: FaCommentDots },
];

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
    isActive
      ? "bg-[#5932EA]/10 text-[#5932EA] font-medium"
      : "text-gray-700 dark:text-gray-300 hover:text-[#5932EA] hover:bg-[#5932EA]/10"
  }`;

const NavAvatar: React.FC<{
  src?: string | null;
  name?: string;
  className?: string;
}> = ({ src, name, className = "w-7 h-7" }) => {
  const [imgError, setImgError] = useState(false);
  const initials = (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");

  if (src && !imgError) {
    return (
      <img
        src={src}
        alt=""
        onError={() => setImgError(true)}
        className={`${className} rounded-full object-cover shrink-0 ring-2 ring-white dark:ring-gray-800 shadow-sm`}
      />
    );
  }

  if (initials) {
    return (
      <span
        className={`${className} rounded-full shrink-0 flex items-center justify-center text-[10px] font-semibold bg-[#5932EA]/15 text-[#5932EA] ring-2 ring-white dark:ring-gray-800`}
        aria-hidden
      >
        {initials}
      </span>
    );
  }

  return <FaUserCircle className={`${className} text-gray-400 dark:text-gray-500 shrink-0`} />;
};

const AppSidebar: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin, profile } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const displayName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ").trim();
  const profilePicture = profile?.profilePicture as string | undefined;

  const handleLogout = async () => {
    await signOut(auth);
    onClose();
    navigate("/login");
  };

  const renderItems = (items: NavItem[]) =>
    items.map(({ to, labelKey, icon: Icon }) => (
      <li key={to}>
        <NavLink to={to} className={linkClass} onClick={onClose}>
          <Icon className="w-5 h-5 shrink-0" />
          {t(labelKey)}
        </NavLink>
      </li>
    ));

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={onClose} aria-hidden />
      )}

      <aside
        className={`fixed inset-y-0 left-0 w-64 max-w-[85vw] z-40 flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700 shrink-0 gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/imgs/logo-128x128.png"
              alt=""
              width={48}
              height={48}
              className="w-12 h-12 rounded-xl shrink-0"
            />
            <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100 truncate">
              Praisify
            </h1>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shrink-0"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-4">
          <ul className="space-y-1">{renderItems(MEMBER_ITEMS)}</ul>

          {isAdmin && (
            <>
              <p className="mt-6 mb-2 px-4 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                {t("administration")}
              </p>
              <ul className="space-y-1">{renderItems(ADMIN_ITEMS)}</ul>
            </>
          )}
        </nav>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3 shrink-0">
          <NavLink
            to="/profile"
            onClick={onClose}
            className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-[#5932EA]/10 transition-colors"
          >
            <NavAvatar src={profilePicture} name={displayName} className="w-9 h-9" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                {displayName || t("profile")}
              </p>
              {profile?.email && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {profile.email}
                </p>
              )}
            </div>
          </NavLink>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2.5 rounded-xl text-gray-500 dark:text-gray-400 hover:text-[#5932EA] hover:bg-[#5932EA]/10 dark:hover:bg-[#5932EA]/20 transition-colors"
              title={theme === "dark" ? t("lightMode") : t("darkMode")}
              aria-label={theme === "dark" ? t("lightMode") : t("darkMode")}
            >
              {theme === "dark" ? <FaSun className="w-5 h-5" /> : <FaMoon className="w-5 h-5" />}
            </button>
            {isAdmin && (
              <button
                onClick={() => {
                  setShowSettingsModal(true);
                  onClose();
                }}
                className="p-2.5 rounded-xl text-gray-500 dark:text-gray-400 hover:text-[#5932EA] hover:bg-[#5932EA]/10 dark:hover:bg-[#5932EA]/20 transition-colors"
                title={t("settings")}
                aria-label={t("settings")}
              >
                <FaCog className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:text-red-600 hover:bg-red-500/10 transition-colors"
            >
              <FaSignOutAlt className="w-5 h-5 shrink-0" />
              {t("logout")}
            </button>
          </div>
        </div>
      </aside>

      {isAdmin && (
        <SettingsModal isOpen={showSettingsModal} onClose={() => setShowSettingsModal(false)} />
      )}
    </>
  );
};

export default AppSidebar;
